import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import Layout from '../../../../layouts';
import Page from '../../../../components/Page';
import axiosInstance from '../../../../utils/axios';
import { BotaoExportar, Num, CABECALHO_TABELA } from '../../../../components/gps';
import Modal, { BotaoPrimario, BotaoSecundario, Campo, classesEntrada } from '../../../../components/gps/Modal';
import TituloTela from '../../../../components/gps/TituloTela';
import { AutoCompleteUnico, buscarPaginado } from '../../../../components/gps/AutoComplete';

const buscarUsuarios = buscarPaginado(
  (termo, pagina, linhas) =>
    `users/paginado?page=${pagina}&rows=${linhas}&valor=${encodeURIComponent(termo)}` +
    `&campo=nome&direcao=asc&ordenar=nome&status=1&regional=null`,
  (u) => ({ codigo: u.codigo, rotulo: u.nome, secundario: u.email || '—', dado: u })
);

/* ============================================================================
   Acessos de Regionais - visual novo (shadcn + Tailwind).

   Contratos mantidos, conferidos no que a tela anterior chamava:
     GET  regionais/acessos/pendentes
     GET  regionais/com-acesso            -> filtra por `podeEditar`
     GET  users/paginado?...&status=1     -> busca de usuario
     POST regionais/acessos/conceder      { usuarioId, regionalId }
     POST regionais/acessos/:id/aprovar | :id/negar

   TRES MUDANCAS DE COMPORTAMENTO, todas com motivo:

   1. NEGAR PEDE CONFIRMACAO. Aprovar e negar estavam lado a lado, mesmo tamanho,
      um clique cada. Negar tira o acesso de alguem que esta esperando para
      trabalhar, e nao ha desfazer na tela - so refazer o pedido e esperar de
      novo. Aprovar segue em um clique: errar para o lado do acesso e recuperavel
      (basta remover), errar para o lado da recusa custa o dia de alguem.

   2. O PRAZO E ORDENADO E DESTACADO. A solicitacao expira em 5 dias e desaparece;
      a lista vinha na ordem do backend, então a que estava para vencer podia
      estar no fim. Agora as mais urgentes vem primeiro, e "expira hoje" e
      vermelho - e o unico estado em que ficar sem responder tem consequencia.

   3. A busca de usuario nao usa Autocomplete do MUI. E um campo de texto com
      lista filtrada: a consulta vai ao backend com atraso (nao a cada tecla) e o
      resultado ja vem limitado a 20 pelo proprio endpoint.
   ============================================================================ */

type UsuarioOption = { codigo: number; nome: string; email?: string };
type RegionalOption = { codigo: number; nome: string; podeEditar?: boolean };
type Pendente = {
  codigo: number;
  regionalId: number;
  regionalNome: string;
  usuarioId: number;
  usuarioNome: string;
  usuarioEmail: string;
  status: string;
  dataSolicitacao: string;
  dataLimite: string;
};

const GRADE = 'minmax(200px,2.2fr) minmax(150px,1.4fr) 160px 150px 190px';

RegionaisAcessosPage.getLayout = function getLayout(page: React.ReactElement) {
  return <Layout>{page}</Layout>;
};

/* Negativo = ja expirou. Antes o `Math.max(0, ...)` dava 0 tanto para "vence
   hoje" quanto para "ja venceu" (o job de expiracao roda de hora em hora), e o
   vencido aparecia como "expira hoje". */
function diasRestantes(dataLimite: string): number {
  const limite = new Date(dataLimite).getTime();
  const falta = limite - Date.now();
  if (falta <= 0) return -1;
  return Math.ceil(falta / (24 * 60 * 60 * 1000));
}

export default function RegionaisAcessosPage() {
  const { enqueueSnackbar } = useSnackbar();

  const [regionais, setRegionais] = useState<RegionalOption[]>([]);
  const [usuarioSel, setUsuarioSel] = useState<UsuarioOption | null>(null);
  const [regionalSel, setRegionalSel] = useState<number | ''>('');
  const [salvando, setSalvando] = useState(false);

  const [pendentes, setPendentes] = useState<Pendente[]>([]);
  const [carregando, setCarregando] = useState(false);
  const [erroCarga, setErroCarga] = useState('');

  const [modalConceder, setModalConceder] = useState(false);
  const [negando, setNegando] = useState<Pendente | null>(null);
  const [respondendo, setRespondendo] = useState<number | null>(null);

  const carregarPendentes = useCallback(async () => {
    setCarregando(true);
    setErroCarga('');
    try {
      const res = await axiosInstance.get<Pendente[]>('regionais/acessos/pendentes');
      setPendentes(Array.isArray(res.data) ? res.data : []);
    } catch (e: any) {
      // Falha NAO e "nenhuma pendente": o admin concluia que nao havia nada a
      // aprovar enquanto as solicitacoes expiravam.
      setPendentes([]);
      setErroCarga(e?.response?.data?.message || 'Não foi possível carregar as solicitações.');
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    carregarPendentes();
    // regionais que o admin pode gerenciar
    axiosInstance
      .get<RegionalOption[]>('regionais/com-acesso')
      .then((res) => {
        const lista = Array.isArray(res.data) ? res.data : [];
        setRegionais(lista.filter((r) => r.podeEditar));
      })
      .catch(() => setRegionais([]));
  }, [carregarPendentes]);

  // Mais urgente primeiro: a solicitacao expira em 5 dias e some. Deixar a que
  // vence hoje no fim da lista e o mesmo que esconder.
  const ordenados = useMemo(
    () =>
      [...pendentes].sort(
        (a, b) => new Date(a.dataLimite).getTime() - new Date(b.dataLimite).getTime()
      ),
    [pendentes]
  );

  const vencendo = useMemo(() => ordenados.filter((p) => diasRestantes(p.dataLimite) <= 1).length, [ordenados]);

  const conceder = async () => {
    if (!usuarioSel?.codigo || !regionalSel) {
      enqueueSnackbar('Selecione o usuário e a regional.', { variant: 'warning' });
      return;
    }
    setSalvando(true);
    try {
      await axiosInstance.post('regionais/acessos/conceder', {
        usuarioId: usuarioSel.codigo,
        regionalId: Number(regionalSel),
      });
      // O servidor cria uma solicitacao PENDENTE; o acesso so vale depois de aprovada.
      enqueueSnackbar('Solicitação criada. O acesso só vale depois de aprovado na lista abaixo (prazo de 5 dias).', { variant: 'success' });
      setUsuarioSel(null);
      setRegionalSel('');
      setModalConceder(false);
      await carregarPendentes();
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.message || 'Erro ao conceder acesso.', { variant: 'error' });
    } finally {
      setSalvando(false);
    }
  };

  const responder = async (id: number, aprovar: boolean) => {
    setRespondendo(id);
    try {
      await axiosInstance.post(`regionais/acessos/${id}/${aprovar ? 'aprovar' : 'negar'}`, {});
      enqueueSnackbar(aprovar ? 'Acesso aprovado.' : 'Solicitação negada.', { variant: 'success' });
      setNegando(null);
      await carregarPendentes();
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.message || 'Erro ao responder solicitação.', { variant: 'error' });
    } finally {
      setRespondendo(null);
    }
  };

  return (
    <Page title="Regionais - Acessos">
      <div className="mx-auto w-full max-w-[var(--gps-pagina-max)] px-4 pb-2 pt-2 font-sans text-ink">
        <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
          <div>
            <TituloTela
              descricao={
                <>
Cada solicitação vale por <strong>5 dias</strong> e depois expira sozinha. Sem regional
              aprovada, o usuário entra no sistema e não enxerga grupo, dispositivo nem política.
                </>
              }
            >
              Acessos de regionais
            </TituloTela>
          </div>

          <div className="flex flex-none items-center gap-2">
            <BotaoExportar
              nomeArquivo="acessos-pendentes"
              linhas={() =>
                ordenados.map((s) => ({
                  Regional: s.regionalNome ?? '',
                  Usuario: s.usuarioNome ?? '',
                  'E-mail': s.usuarioEmail ?? '',
                  Status: s.status ?? '',
                  Solicitado: s.dataSolicitacao ? new Date(s.dataSolicitacao).toLocaleString('pt-BR') : '',
                  'Expira em': s.dataLimite ? new Date(s.dataLimite).toLocaleString('pt-BR') : '',
                }))
              }
            />
            <button
              type="button"
              onClick={() => setModalConceder(true)}
              className="h-9 flex-none rounded-lg bg-primary px-4 text-[12.5px] font-bold text-primary-foreground"
            >
              + Conceder acesso
            </button>
          </div>
        </div>

        <div className="mb-5 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <div className="rounded-lg border border-line bg-surface p-4 shadow-card">
            <span className="text-[11px] font-bold uppercase tracking-[0.07em] text-faint">Pendentes</span>
            <div className="mt-2 flex items-baseline gap-2">
              <Num className="text-2xl font-extrabold leading-none text-ink">{ordenados.length}</Num>
              <span className="text-xs text-muted-foreground">aguardando você</span>
            </div>
          </div>
          <div className="rounded-lg border border-line bg-surface p-4 shadow-card">
            <span className="text-[11px] font-bold uppercase tracking-[0.07em] text-faint">
              Vencendo em 24h
            </span>
            <div className="mt-2 flex items-baseline gap-2">
              <Num
                className={`text-2xl font-extrabold leading-none ${vencendo > 0 ? 'text-danger' : 'text-ink'}`}
              >
                {vencendo}
              </Num>
              <span className="text-xs text-muted-foreground">expiram sem resposta</span>
            </div>
          </div>
          <div className="rounded-lg border border-line bg-surface p-4 shadow-card">
            <span className="text-[11px] font-bold uppercase tracking-[0.07em] text-faint">
              Regionais que você gerencia
            </span>
            <div className="mt-2 flex items-baseline gap-2">
              <Num className="text-2xl font-extrabold leading-none text-ink">{regionais.length}</Num>
              <span className="text-xs text-muted-foreground">pode conceder</span>
            </div>
          </div>
        </div>

        <div className="overflow-hidden rounded-lg border border-primary/25 bg-surface shadow-card">
          <div className="flex items-center justify-between border-b border-line bg-surface-2 px-3.5 py-2">
            <span className="text-[12px] font-bold">Solicitações pendentes</span>
            <button
              type="button"
              onClick={carregarPendentes}
              className="h-7 rounded-md border border-line px-2.5 text-[12px] font-semibold text-muted-foreground hover:text-ink"
            >
              Atualizar
            </button>
          </div>

          <div className="overflow-x-auto">
            <div className="min-w-[900px]">
              <div
                className={CABECALHO_TABELA}
                style={{ gridTemplateColumns: GRADE }}
              >
                <div>Usuário</div>
                <div>Regional</div>
                <div>Solicitado em</div>
                <div>Prazo</div>
                <div className="text-right">Ações</div>
              </div>

              {carregando && (
                <div className="p-8 text-center text-[12.5px] text-muted-foreground">Carregando…</div>
              )}

              {!carregando && !!erroCarga && (
                <div className="p-8 text-center text-[12.5px] text-danger">
                  {erroCarga}{' '}
                  <button type="button" className="font-semibold underline" onClick={carregarPendentes}>
                    tentar de novo
                  </button>
                </div>
              )}

              {!carregando && !erroCarga && !ordenados.length && (
                <div className="p-8 text-center text-[12.5px] text-muted-foreground">
                  Nenhuma solicitação pendente.
                </div>
              )}

              {!carregando &&
                ordenados.map((p) => {
                  const dias = diasRestantes(p.dataLimite);
                  const urgente = dias <= 1;
                  return (
                    <div
                      key={p.codigo}
                      className="grid items-center border-b px-3.5 py-2 hover:bg-surface-2"
                      style={{ gridTemplateColumns: GRADE, borderColor: 'var(--gps-soft)' }}
                    >
                      <div className="flex min-w-0 items-center gap-2.5 pr-3">
                        <span className="min-w-0">
                          <span className="block truncate text-[12.5px] font-semibold">{p.usuarioNome}</span>
                          <span className="block truncate text-[11px] text-muted-foreground">
                            {p.usuarioEmail}
                          </span>
                        </span>
                      </div>

                      <span className="min-w-0 truncate pr-3 text-[12px]">{p.regionalNome}</span>

                      <Num className="text-[12px] text-muted-foreground">
                        {new Date(p.dataSolicitacao).toLocaleString('pt-BR')}
                      </Num>

                      <div>
                        <span
                          className={`inline-flex h-[22px] items-center rounded-md px-2 text-[11px] font-bold tracking-wide ${
                            urgente ? 'bg-danger-soft text-danger' : dias <= 2 ? 'bg-warn-soft text-warn' : 'bg-soft text-muted-foreground'
                          }`}
                        >
                          {dias < 0 ? 'expirado' : dias === 1 ? 'vence em até 1 dia' : `${dias} dias`}
                        </span>
                      </div>

                      <div className="flex items-center justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => responder(p.codigo, true)}
                          disabled={respondendo === p.codigo}
                          className="h-8 rounded-lg bg-ok px-3 text-[12px] font-bold text-white disabled:opacity-40"
                        >
                          {respondendo === p.codigo ? '...' : 'Aprovar'}
                        </button>
                        <button
                          type="button"
                          onClick={() => setNegando(p)}
                          disabled={respondendo === p.codigo}
                          className="h-8 rounded-lg border border-line px-3 text-[12px] font-semibold text-danger disabled:opacity-40"
                        >
                          Negar
                        </button>
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        </div>
      </div>

      <Modal
        aberto={modalConceder}
        titulo="Conceder acesso"
        descricao="O acesso entra como solicitação e vale por 5 dias até ser aprovado."
        icone="+"
        onFechar={() => setModalConceder(false)}
        rodape={
          <>
            <BotaoSecundario onClick={() => setModalConceder(false)} disabled={salvando}>
              Cancelar
            </BotaoSecundario>
            <BotaoPrimario onClick={conceder} disabled={salvando || !usuarioSel || !regionalSel}>
              {salvando ? 'Enviando...' : 'Conceder'}
            </BotaoPrimario>
          </>
        }
      >
        {/* USUARIO POR LUPA (21/10/2026). Antes consultava 350 ms depois de cada
            pausa na digitacao; agora a lupa abre o seletor paginado, com o mesmo
            endpoint e filtros (so ativos). A corrida entre respostas, que aqui
            importava porque se concede ACESSO A REGIONAL, fica a cargo do
            ModalSelecionar (so a ultima consulta escreve na lista). */}
        <Campo bloco rotulo="Usuário" obrigatorio dica="Busque pelo nome. Só usuários ativos aparecem.">
          <AutoCompleteUnico
            selecionado={
              usuarioSel ? { codigo: usuarioSel.codigo, rotulo: usuarioSel.nome, secundario: usuarioSel.email || '—' } : null
            }
            aoEscolher={(o) => setUsuarioSel(o ? o.dado : null)}
            buscar={buscarUsuarios}
            titulo="Selecionar usuário"
            placeholder="Nome do usuário (Enter ou lupa)"
            vazio="Nenhum usuário encontrado."
          />
        </Campo>

        <Campo rotulo="Regional" obrigatorio>
          <select
            value={regionalSel}
            onChange={(e) => setRegionalSel(e.target.value ? Number(e.target.value) : '')}
            className={classesEntrada}
          >
            <option value="">Selecione…</option>
            {regionais.map((r) => (
              <option key={r.codigo} value={r.codigo}>
                {r.nome}
              </option>
            ))}
          </select>
          {!regionais.length && (
            <span className="mt-1 block text-[11px] text-danger">
              Você não gerencia nenhuma regional — não há onde conceder acesso.
            </span>
          )}
        </Campo>
      </Modal>

      {/* Negar confirma; aprovar nao. Assimetria de propósito: aprovar errado se
          desfaz removendo o acesso, negar errado custa o dia de quem está
          esperando para trabalhar e só se resolve refazendo o pedido. */}
      <Modal
        aberto={Boolean(negando)}
        titulo="Negar solicitação?"
        descricao="A pessoa continua sem acesso à regional e precisará pedir de novo."
        icone="!"
        largura="max-w-[460px]"
        onFechar={() => setNegando(null)}
        rodape={
          <>
            <BotaoSecundario onClick={() => setNegando(null)} disabled={respondendo !== null}>
              Voltar
            </BotaoSecundario>
            <button
              type="button"
              onClick={() => negando && responder(negando.codigo, false)}
              disabled={respondendo !== null}
              className="h-9 rounded-lg bg-danger px-4 text-[12.5px] font-bold text-white disabled:opacity-40"
            >
              {respondendo !== null ? 'Negando...' : 'Negar acesso'}
            </button>
          </>
        }
      >
        {negando && (
          <div className="rounded-lg border border-line bg-surface-2 p-3 text-[12px]">
            <div className="font-semibold">{negando.usuarioNome}</div>
            <div className="text-muted-foreground">{negando.usuarioEmail}</div>
            <div className="mt-2 text-muted-foreground">
              Regional: <strong className="text-ink">{negando.regionalNome}</strong>
            </div>
          </div>
        )}
      </Modal>
    </Page>
  );
}
